import React, { forwardRef } from 'react';
import { StructuredText, renderNodeRule } from 'react-datocms';
import { isLink } from 'datocms-structured-text-utils';
import { defaultMetaTransformer } from 'datocms-structured-text-generic-html-renderer';
import { GatsbyImage } from 'gatsby-plugin-image';
import { className } from 'design-system';

import TransitionLink from '../TransitionLink';

import style from './styles.module.scss';

const HtmlComponent = forwardRef((props, ref) => {
    return (
        <div
            ref={ref}
            className={`${style.content} ${props.className ? props.className : ''
                }`}
            {...(props.children
                ? { dangerouslySetInnerHTML: { __html: props.children } }
                : {})}>
            {props.content ? (
                <StructuredText
                    data={props.content}
                    customNodeRules={[
                        renderNodeRule(isLink, ({ node, children, key }) => {
                            return (
                                <a
                                    key={key}
                                    {...(!!node.meta
                                        ? defaultMetaTransformer({
                                            node,
                                            meta: node.meta,
                                        })
                                        : {})}
                                    href={node.url.replace(
                                        'https://www.datocms-assets.com',
                                        process.env.GATSBY_CDN_URL,
                                    )}>
                                    {children}
                                </a>
                            );
                        }),
                    ]}
                    renderInlineRecord={({ record }) => {
                        switch (record?.__typename) {
                            case 'DatoCmsFile':
                                return (
                                    <a
                                        target="_blank"
                                        alt={
                                            record.file.alt ||
                                            record.file.title ||
                                            record.file.filename
                                        }
                                        href={record.file.url}>
                                        {record.file.title ||
                                            record.file.filename}
                                    </a>
                                );
                            case 'DatoCmsNews':
                            case 'DatoCmsStudyCase':
                            case 'DatoCmsArticle':
                            case 'DatoCmsPage':
                                return (
                                    <TransitionLink
                                        to={record.slug}
                                        title={record.title}>
                                        {record.title}
                                    </TransitionLink>
                                );
                            default:
                                return null;
                        }
                    }}
                    renderLinkToRecord={({
                        record,
                        children,
                        transformedMeta = {},
                    }) => {
                        switch (record.__typename) {
                            case 'DatoCmsFile':
                                return (
                                    <a
                                        target="_blank"
                                        alt={
                                            record.file.alt ||
                                            record.file.title ||
                                            record.file.filename
                                        }
                                        {...transformedMeta}
                                        href={record.file.url}>
                                        {children}
                                    </a>
                                );
                            case 'DatoCmsNews':
                            case 'DatoCmsStudyCase':
                            case 'DatoCmsArticle':
                            case 'DatoCmsPage':
                                return (
                                    <TransitionLink
                                        title={record.title}
                                        {...transformedMeta}
                                        to={record.slug}>
                                        {children}
                                    </TransitionLink>
                                );
                            default:
                                return null;
                        }
                    }}
                    renderBlock={({ record }) => {
                        switch (record?.__typename) {
                            case 'DatoCmsImage':
                                let item = record.image ? (
                                    record.image?.gatsbyImageData ? (
                                        <GatsbyImage
                                            alt={record.image.alt || ''}
                                            image={record.image.gatsbyImageData}
                                        />
                                    ) : record.image?.url ? (
                                        <img
                                            {...className(
                                                `img-fluid ${style.image}`,
                                            )}
                                            alt={record.image.alt || ''}
                                            src={record.image.url}
                                        />
                                    ) : null
                                ) : record.externalSource ? (
                                    <img
                                        {...className(
                                            `img-fluid ${style.image}`,
                                        )}
                                        src={record.externalSource}
                                    />
                                ) : null;

                                if (!item) {
                                    return null;
                                }

                                return record.link?.url ||
                                    record.externalLink ? (
                                    <a
                                        href={
                                            record.link?.url ||
                                            record.externalLink
                                        }
                                        target="_blank"
                                        rel={
                                            record.externalLink
                                                ? 'noreferrer noopener'
                                                : ''
                                        }>
                                        {item}
                                    </a>
                                ) : (
                                    item
                                );
                            case 'DatoCmsSnippet':
                                return (
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: record.snippet,
                                        }}
                                    />
                                );
                            case 'DatoCmsVideoEmbed':
                                switch (record.video?.provider) {
                                    case 'youtube':
                                        return (
                                            <div className={style.videoWrapper}>
                                                <iframe
                                                    width={record.video.width}
                                                    height={record.video.height}
                                                    src={`https://www.youtube.com/embed/${record.video.providerUid}`}
                                                    title={record.video.title}
                                                    frameBorder="0"
                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                    allowFullScreen
                                                />
                                            </div>
                                        );
                                    default:
                                        return null;
                                }
                            default:
                                return null;
                        }
                    }}
                />
            ) : null}
        </div>
    );
});

export default HtmlComponent;
